import dayjs from 'dayjs';
import { BioType, BIO_LIMITS } from './constants';

/** Gestational age at a reference date (ultrasound / study), from an LMP calendar date (YYYY-MM-DD). */
export function computeGAFromLmp(
  lmpStr: string,
  refDateStr?: string
): { weeks: number; days: number } | null {
  if (!lmpStr) return null;
  const ref = (refDateStr ? dayjs(refDateStr) : dayjs()).startOf('day');
  const lmpDay = dayjs(lmpStr).startOf('day');
  const diffDays = ref.diff(lmpDay, 'day');
  if (diffDays < 0) return null;
  return { weeks: Math.floor(diffDays / 7), days: diffDays % 7 };
}

export const calculateGA = (type: BioType, valueStr: string): { ga: string, totalDays: number, error: boolean } | null => {
    const val = parseFloat(valueStr);
    const limit = BIO_LIMITS[type];
    
    if (!valueStr || isNaN(val)) return null;
    if (val < limit.min || val > limit.max) return { ga: 'Range Error', totalDays: 0, error: true };

    let gaWeeks = 0;
    if (type === 'BPD') gaWeeks = 9.54 + 1.482 * val + 0.1676 * (val * val);
    else if (type === 'HC') gaWeeks = 8.96 + 0.54 * val + 0.0003 * Math.pow(val, 3);
    else if (type === 'AC') gaWeeks = 8.14 + 0.753 * val + 0.0036 * (val * val);
    else if (type === 'FL') gaWeeks = 10.35 + 2.46 * val + 0.17 * (val * val);
    else if (type === 'CRL') {
        const val_cm = val / 10.0; 
        const exponent = 1.684969 + (0.315646 * val_cm) - (0.049306 * Math.pow(val_cm, 2)) + (0.004057 * Math.pow(val_cm, 3)) - (0.000120456 * Math.pow(val_cm, 4));
        gaWeeks = Math.exp(exponent);
    }

    const totalDays = gaWeeks * 7;
    const w = Math.floor(totalDays / 7);
    const d = Math.floor(totalDays % 7);

    if (w < 4 || w > 44) return { ga: 'Inv. Calc', totalDays: 0, error: true };
    return { ga: `${w}w ${d}d`, totalDays: totalDays, error: false };
};

type BiometryFormSlice = {
  crl?: string;
  bpd?: string;
  hc?: string;
  ac?: string;
  fl?: string;
};

/** Total gestational days from biometry fields (CRL preferred when set). */
export function deriveTotalDaysFromBiometryForm(
  form: BiometryFormSlice,
  preferCrl = true
): number | null {
  const hasCrl = !!form.crl && parseFloat(form.crl) > 0;
  if (preferCrl && hasCrl) {
    const res = calculateGA('CRL', form.crl!);
    return res && !res.error ? res.totalDays : null;
  }

  let sumDays = 0;
  let count = 0;
  (['BPD', 'HC', 'AC', 'FL'] as BioType[]).forEach((field) => {
    const val = form[field.toLowerCase() as keyof BiometryFormSlice];
    if (val) {
      const res = calculateGA(field, val);
      if (res && !res.error) {
        sumDays += res.totalDays;
        count++;
      }
    }
  });
  return count > 0 ? sumDays / count : null;
}

export function applyDatingFromTotalDays(
  totalDays: number,
  anchor: dayjs.Dayjs
): {
  gaWeeks: string;
  gaDays: string;
  lmp: dayjs.Dayjs;
  edd: dayjs.Dayjs;
} {
  const roundedTotalDays = Math.round(totalDays);
  const wNum = Math.floor(roundedTotalDays / 7);
  const dNum = roundedTotalDays % 7;
  const lmp = anchor.subtract(roundedTotalDays, 'day').startOf('day');
  return {
    gaWeeks: wNum.toString(),
    gaDays: dNum.toString(),
    lmp,
    edd: lmp.add(280, 'day').startOf('day'),
  };
}

export function isLmpPlausibleAtReference(
  lmpStr: string,
  refDateStr: string,
  maxWeeks = 44
): boolean {
  const computed = computeGAFromLmp(lmpStr, refDateStr);
  if (!computed) return false;
  return computed.weeks <= maxWeeks;
}
export const getVideoMetadata = (file: File): Promise<{ width: number; height: number; duration: number }> => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        const objectUrl = window.URL.createObjectURL(file);
        
        // Validate that objectUrl is a blob URL (safe for DOM src assignment)
        if (!objectUrl.startsWith('blob:')) {
            window.URL.revokeObjectURL(objectUrl);
            resolve({ width: 0, height: 0, duration: 0 });
            return;
        }
        
        video.preload = 'metadata';
        video.onloadedmetadata = () => { 
            window.URL.revokeObjectURL(objectUrl); 
            resolve({ width: video.videoWidth, height: video.videoHeight, duration: video.duration }); 
        };
        video.onerror = () => { 
            window.URL.revokeObjectURL(objectUrl);
            resolve({ width: 0, height: 0, duration: 0 }); 
        };
        // Safe: objectUrl is validated as blob URL from createObjectURL
        video.src = objectUrl;
    });
};